import { useContext, useEffect, useState } from "react";
import { AppContext } from "../App";
import { callApi } from "../Helpers";
import { FaChevronDown } from "react-icons/fa";
import { getInlineLoader } from "../Helpers";
import ClassroomRegistrationForm from "../ones/ClassroomRegistrationForm";
import StreamRegistrationForm from "../ones/StreamRegistrationForm";
import OneStream from "../ones/OneStream";
import OneSubject from "../ones/OneSubject";
import OneExamCycle from "../ones/OneExamCycle";
import ExamCycleCreationForm from "../ones/ExamCycleCreationForm";

export default function View({ uuid }) {
  const appContext = useContext(AppContext);

  const [ready, setReady] = useState(false);
  const [data, setData] = useState(null);
  const [classroomStreams, setClassroomStreams] = useState(null);
  const [examCycles, setExamCycles] = useState(null);
  const [loading, setLoading] = useState(false);

  async function init() {
    setReady(false);
    await Promise.all([
      getData(),
      getClassroomStreams(),
      getExamCycles(),
      appContext.getAllStaff(),
    ]);
    setReady(true);
  }

  async function getData() {
    await callApi("get-classroom-data", { uuid }).then((response) => {
      if (response.status === 1) {
        setData(response.data);
      } else {
        appContext.tellError("Could not fetch data, check your connection");
      }
    });
  }

  async function getClassroomStreams() {
    await callApi("get-classroom-streams", { uuid }).then((response) => {
      if (response.status === 1) {
        setClassroomStreams(response.data);
      } else {
        appContext.tellError("Could not fetch data, check your connection");
      }
    });
  }

  async function getExamCycles() {
    await callApi("get-classroom-exam-cycles", { uuid }).then((response) => {
      if (response.status === 1) {
        setExamCycles(response.data);
      } else {
        appContext.tellError("Could not fetch data, check your connection");
      }
    });
  }

  function getSubjectMasterUuid(subjectData, classOrStreamUuid) {
    const masters = JSON.parse(subjectData.masters);
    return masters[classOrStreamUuid];
  }

  function getStaffName(_staffUuid) {
    let name = "";
    if (appContext.allStaff) {
      for (const staff of appContext.allStaff) {
        if (staff.uuid === _staffUuid) {
          name = `${staff.firstName} ${staff.middleName} ${staff.lastName}`;
        }
      }
    }
    return name;
  }

  async function updateSubjectMaster(subjectUuid, classOrStreamUuid, masterUuid) {
    if (masterUuid && masterUuid.trim().length > 0) {
      setLoading(true);
      await callApi("update-subject-master", {
        subjectUuid,
        classOrStreamUuid,
        masterUuid,
      }).then((response) => {
        if (response.status === 1) {
          getData();
          appContext.tellMessage("Successful");
        } else {
          appContext.tellError(response.msg);
        }
      });
      setLoading(false);
    } else {
      appContext.tellError("Invalid subject master");
    }
  }

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    appContext.setShowOverlayLoader(loading);
    return () => {
      appContext.setShowOverlayLoader(false);
    };
  }, [loading]);

  if (ready && data) {
    return (
      <div className="container">
        <div className="row">
          <div className="col-12 mb-4">
            <h6 className="main-section-title">
              {data.name}
              <br />
              <span className="badge bg-primary">Classroom</span>
            </h6>
            <hr />
          </div>

          <div className="col-md-4 mt-2 mb-2">
            <label className="form-label">Name</label>
            <h6 style={{ fontSize: "18px" }}>{data.name}</h6>
          </div>

          <div className="col-md-4 mt-2 mb-2">
            <label className="form-label">Supports Streams?</label>
            <h6 style={{ fontSize: "18px", margin: "0" }}>{data.applyStreams}</h6>
          </div>

          {/* Allocated Subjects */}
          <div className="col-md-12 mt-2 mb-2">
            <div className="card">
              <div className="card-body">
                <div
                  className="d-flex justify-content-between"
                  style={{ cursor: "pointer" }}
                  data-bs-toggle="collapse"
                  data-bs-target="#_classroomSection1"
                  aria-expanded="false"
                  aria-controls="_classroomSection1"
                >
                  <h6 className="card-title align-self-center">Allocated Subjects</h6>
                  <FaChevronDown size={24} className="align-self-center text-primary" />
                </div>

                <div className="collapse" id="_classroomSection1">
                  <hr />
                  {data.applyStreams === "no" ? (
                    <>
                      {data.supportedSubjectsData &&
                      data.supportedSubjectsData.length > 0 ? (
                        data.supportedSubjectsData.map((item, i) => (
                          <OneSubject key={i} data={item} />
                        ))
                      ) : (
                        <h6 style={{ fontSize: "14px" }}>No subjects were found</h6>
                      )}
                    </>
                  ) : (
                    <h6 style={{ fontSize: "14px" }}>
                      Manage allocated subjects in specific streams
                    </h6>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Subject Masters */}
          <div className="col-md-12 mt-2 mb-2">
            <div className="card">
              <div className="card-body">
                <div
                  className="d-flex justify-content-between"
                  style={{ cursor: "pointer" }}
                  data-bs-toggle="collapse"
                  data-bs-target="#_classroomSection2"
                  aria-expanded="false"
                  aria-controls="_classroomSection2"
                >
                  <h6 className="card-title align-self-center">Subject Masters</h6>
                  <FaChevronDown size={24} className="align-self-center text-primary" />
                </div>

                <div className="collapse" id="_classroomSection2">
                  <hr />
                  {data.applyStreams === "no" ? (
                    <>
                      {data.supportedSubjectsData &&
                      data.supportedSubjectsData.length > 0 ? (
                        data.supportedSubjectsData.map((item, i) => (
                          <div className="row" key={i}>
                            <div className="col-md-6 mt-2 mb-2">
                              <label className="form-label">Subject Name</label>
                              <h6 style={{ fontSize: "18px" }}>{item.name}</h6>
                            </div>

                            <div className="col-md-6 mt-2 mb-2">
                              <label className="form-label">Subject Master</label>
                              <h6 style={{ fontSize: "18px" }}>
                                {getSubjectMasterUuid(item, uuid)
                                  ? getStaffName(getSubjectMasterUuid(item, uuid))
                                  : "N/A"}
                              </h6>
                            </div>

                            <div className="col-md-12 mt-2 mb-2">
                              <label className="form-label">Select Subject Master</label>
                              <select className="form-control" id={`${item.uuid}`}>
                                <option value=""></option>
                                {appContext.allStaff.map((staff, j) => (
                                  <option key={j} value={staff.uuid}>
                                    {staff.firstName} {staff.middleName} {staff.lastName}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div className="col-md-12 mt-2 mb-2 text-end">
                              <button
                                className="btn btn-dark btn-sm"
                                onClick={() =>
                                  updateSubjectMaster(
                                    item.uuid,
                                    uuid,
                                    document.getElementById(item.uuid).value
                                  )
                                }
                              >
                                Update
                              </button>
                              <hr />
                            </div>
                          </div>
                        ))
                      ) : (
                        <h6 style={{ fontSize: "14px" }}>No subjects were found</h6>
                      )}
                    </>
                  ) : (
                    <h6 style={{ fontSize: "14px" }}>
                      Manage subject masters in specific streams
                    </h6>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Examination Cycles */}
          <div className="col-md-12 mt-2 mb-2">
            <div className="card">
              <div className="card-body">
                <div
                  className="d-flex justify-content-between"
                  style={{ cursor: "pointer" }}
                  data-bs-toggle="collapse"
                  data-bs-target="#_classroomSection3"
                  aria-expanded="false"
                  aria-controls="_classroomSection3"
                >
                  <h6 className="card-title align-self-center">Examination Cycles</h6>
                  <FaChevronDown size={24} className="align-self-center text-primary" />
                </div>

                <div className="collapse" id="_classroomSection3">
                  <hr />
                  {data.applyStreams === "no" ? (
                    <>
                      <h6 className="sub-section-title">Create New Exam Cycle</h6>
                      <ExamCycleCreationForm
                        classroomUuid={uuid}
                        availableSubjects={data.supportedSubjectsData}
                        callback={getExamCycles}
                      />

                      <hr />

                      <h6 className="sub-section-title">All Exam Cycles</h6>
                      {examCycles && examCycles.length > 0 ? (
                        examCycles.map((item, i) => (
                          <OneExamCycle data={item} key={i} />
                        ))
                      ) : (
                        <h6 style={{ fontSize: "14px" }}>No cycles were found</h6>
                      )}
                    </>
                  ) : (
                    <h6 style={{ fontSize: "14px" }}>
                      Manage examination cycles in specific streams
                    </h6>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Streams */}
          <div className="col-md-12 mt-2 mb-2">
            <div className="card">
              <div className="card-body">
                <div
                  className="d-flex justify-content-between"
                  style={{ cursor: "pointer" }}
                  data-bs-toggle="collapse"
                  data-bs-target="#_classroomSection4"
                  aria-expanded="false"
                  aria-controls="_classroomSection4"
                >
                  <h6 className="card-title align-self-center">Streams</h6>
                  <FaChevronDown size={24} className="align-self-center text-primary" />
                </div>

                <div className="collapse" id="_classroomSection4">
                  <hr />
                  {data.applyStreams === "yes" ? (
                    <div className="row">
                      <div className="col-md-12">
                        <h6 className="sub-section-title">Register Stream</h6>
                        <StreamRegistrationForm
                          classroomUuid={uuid}
                          createCallback={getClassroomStreams}
                        />
                      </div>

                      <div className="col-md-12">
                        <hr />
                      </div>

                      <div className="col-md-12">
                        <h6 className="sub-section-title">Available streams</h6>
                        {classroomStreams && classroomStreams.length > 0 ? (
                          classroomStreams.map((item, i) => (
                            <OneStream key={i} data={item} />
                          ))
                        ) : (
                          <h6>No streams were found</h6>
                        )}
                      </div>
                    </div>
                  ) : (
                    <h6 style={{ fontSize: "14px" }}>
                      Streams are not supported in this classroom
                    </h6>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Edit Classroom */}
          <div className="col-md-12 mt-2 mb-2">
            <div className="card">
              <div className="card-body">
                <div
                  className="d-flex justify-content-between"
                  style={{ cursor: "pointer" }}
                  data-bs-toggle="collapse"
                  data-bs-target="#_classroomSection5"
                  aria-expanded="false"
                  aria-controls="_classroomSection5"
                >
                  <h6 className="card-title align-self-center">Edit Classroom</h6>
                  <FaChevronDown size={24} className="align-self-center text-primary" />
                </div>

                <div className="collapse" id="_classroomSection5">
                  <hr />
                  <ClassroomRegistrationForm callback={getData} data={data} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  } else {
    return (
      <div className="container mSupportLoading">{getInlineLoader()}</div>
    );
  }
}
